<template>
  <div class="upload">
    <div class="upload__content">
      <p class="upload__content__text">
        <b>{{ t('form-upload-drag') }}</b
        ><br />
        <u>{{ t('form-upload-browse') }}</u>
      </p>
      <p class="p-medium upload__content__types">
        PDF, PNG, JPG, DOC (10MB max)
      </p>
    </div>
    <label :for="id"></label>
    <input
      :id="id"
      ref="input"
      type="file"
      :name="name"
      class="upload__input"
    />
  </div>
</template>

<script lang="ts">
/* eslint-disable id-length */
import { onMounted, defineComponent, ref } from 'vue'
import { create, FilePond, registerPlugin } from 'filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import { useChromeStore } from '@/stores/chrome'

registerPlugin(FilePondPluginFileValidateType)
registerPlugin(FilePondPluginFileValidateSize)

export default defineComponent({
  props: {
    name: {
      type: String,
      default: 'file',
    },
    id: {
      type: String,
      default: 'file',
    },
    uploadUrl: {
      type: String,
      default: 'file',
    },
    options: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  setup(props, ctx) {
    const input = ref<HTMLInputElement>()
    const filePond = ref<FilePond>()
    const { t } = useChromeStore()

    onMounted(() => {
      const options = {
        allowMultiple: true,
        acceptedFileTypes: [
          'image/png',
          'image/jpeg',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
        maxTotalFileSize: '10MB',
        credits: false,
        iconRemove: `<svg width="16" height="20" viewBox="0 0 16 20" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M13 6C13.5523 6 14 6.44772 14 7V17C14 18.6569 12.6569 20 11
          20H5C3.34315 20 2 18.6569 2 17V7C2 6.44772 2.44772 6 3 6H13ZM12 8H4V17C4 17.5523 4.44772 18 5 18H11C11.5523
          18 12 17.5523 12 17V8ZM5 1C5 0.447715 5.44772 0 6 0H10C10.5523 0 11 0.447715 11 1V2H15C15.5523 2 16 2.44772
          16 3C16 3.55228 15.5523 4 15 4H1C0.447715 4 0 3.55228 0 3C0 2.44772 0.447715 2 1 2H5V1Z" />
        </svg>`,
      }

      filePond.value = create(input.value, {
        ...options,
        ...props.options,
        server: {
          process: `${import.meta.env.VITE_API_DOMAIN + props.uploadUrl}`,
          revert: `${import.meta.env.VITE_API_DOMAIN + props.uploadUrl}`,
        },
      })

      filePond.value.on('processfile', (error, file) => {
        ctx.emit('fileAdded', file.serverId)
      })
    })

    return { input, filePond, t }
  },
})
</script>

<style lang="scss">
/* stylelint-disable declaration-no-important */
@import 'filepond/dist/filepond.css';

/* stylelint-disable-next-line order/order */
.filepond--root *:not(text) {
  // margin-bottom: 2rem;
  font-size: 1.4rem;
}

.filepond--list {
  left: 0;
  width: 100%;
}

.filepond--item {
  left: 0;
  margin: 0 0 1rem;
}

.filepond--file {
  padding: 0 0 0 2.5rem;
  background: url('/icons/document.svg') no-repeat 1px;
}

[data-filepond-item-state] {
  .filepond--file {
    padding: 0;
    background: none;
  }

  .filepond--file-status {
    flex-shrink: inherit;
  }

  .filepond--file-status * {
    white-space: inherit;
  }

  &.filepond--item {
    margin-bottom: 2rem;
  }

  .filepond--file [data-align*='left'] {
    left: 0;
  }
}

/* stylelint-disable-next-line order/order */
.filepond--item-panel {
  background-color: var(--c-background) !important;
}

.filepond--item > .filepond--panel .filepond--panel-bottom {
  box-shadow: none;
}

.filepond--file,
.filepond--drop-label {
  color: var(--c-foreground);
}

.filepond--drop-label {
  @include get-all-space;

  height: 21.5rem;
  opacity: 0 !important;
  cursor: pointer;
}

.filepond--list-scroller {
  top: 21.5rem;
  width: 100%;
  transform: none !important;
}

.filepond--panel-root {
  background-color: var(--c-background);
  border-radius: 0;
}

.filepond--file-action-button {
  right: 0 !important;
  background: transparent;
  border-radius: 0;

  svg {
    fill: var(--c-foreground);
  }

  &:hover {
    cursor: pointer;
    box-shadow: none;
  }
}
</style>

<style lang="scss" scoped>
.upload {
  position: relative;
  overflow: hidden;
  min-height: 28rem;
  padding: 0 3rem 1rem;
  border: 1px dashed var(--c-foreground);
  border-radius: 8px;
}

.upload__content {
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: calc(100% - 6rem);
  height: 21.5rem;
  color: var(--c-foreground);
  text-align: center;
  border-bottom: 1px dashed var(--c-foreground);
  pointer-events: none;
}

.upload__content__text {
  margin-bottom: 3rem;
}

.upload__content__types {
  opacity: 0.5;
}

.upload__input {
  opacity: 0;
}
</style>
